import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  Home,
  Sparkles,
  MessageCircle,
  Bell,
  CheckCircle2,
  Circle,
  ArrowRight,
  Loader2,
  Send,
  FileText,
  ClipboardList,
  Rocket,
  User,
  Building2,
  Calendar,
  BarChart,
  Settings,
  Scissors,
  Palette,
  Eye,
  Leaf,
  PartyPopper,
  GraduationCap,
  Bug
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Onboarding stages for the roadmap
const ONBOARDING_STAGES = [
  { id: 1, label: "Invite Accepted", icon: CheckCircle2 },
  { id: 2, label: "NDA Signed", icon: FileText },
  { id: 3, label: "Intake Complete", icon: ClipboardList },
  { id: 4, label: "Feedback Submitted", icon: MessageCircle },
  { id: 5, label: "Launch Ready", icon: Rocket }
];

// Onboarding stages (kept for progress display)
export default function PilotPortalDashboard() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  
  const [activeTab, setActiveTab] = useState("welcome");
  
  // Intake form state
  const [intakeStage, setIntakeStage] = useState(1);
  const [intakeResponses, setIntakeResponses] = useState({});
  const [submittingIntake, setSubmittingIntake] = useState(false);
  
  // Chat state
  const [chatMessage, setChatMessage] = useState("");
  const [submittingChat, setSubmittingChat] = useState(false);
  
  // Fetch portal data
  useEffect(() => {
    const fetchPortal = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/pilot/portal/${portalId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (!res.ok) throw new Error("Portal not found");
        const data = await res.json();
        setPortal(data.portal);
        setUser(data.user);
        
        // Load existing intake responses
        if (data.portal.intake_responses) {
          setIntakeResponses(data.portal.intake_responses);
        }
        if (data.portal.intake_stage) {
          setIntakeStage(data.portal.intake_stage);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPortal();
  }, [portalId]);
  
  // Calculate current onboarding stage
  const getCurrentStage = () => {
    if (!portal) return 1;
    if (portal.status === "active" || portal.intake_completed) return 5;
    if (portal.intake_stage >= 4) return 4;
    if (portal.nda_signed) return 3;
    return 2;
  };
  
  // Handle intake form updates
  const updateIntakeField = (field, value) => {
    setIntakeResponses(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle intake section navigation
  const handleNextSection = async () => {
    if (intakeStage < 4) {
      setIntakeStage(prev => prev + 1);
      // Save progress
      await saveIntakeProgress(false);
    } else {
      // Complete intake
      await saveIntakeProgress(true);
    }
  };
  
  const handlePrevSection = () => {
    if (intakeStage > 1) {
      setIntakeStage(prev => prev - 1);
    }
  };
  
  // Save intake progress
  const saveIntakeProgress = async (isComplete = false) => {
    setSubmittingIntake(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/pilot/portal/${portalId}/intake`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          responses: intakeResponses,
          stage: intakeStage,
          is_complete: isComplete
        })
      });
      
      if (!res.ok) throw new Error("Failed to save");
      
      const data = await res.json();
      
      if (isComplete) {
        // Refresh portal data
        setPortal(prev => ({
          ...prev,
          status: "active",
          intake_completed: true
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingIntake(false);
    }
  };
  
  // Send chat/feedback message
  const handleSendMessage = async () => {
    if (!chatMessage.trim()) return;
    
    setSubmittingChat(true);
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/api/admin/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          source: "pilot_portal",
          portal_id: portalId,
          type: "feedback",
          message: chatMessage,
          user_email: user?.email
        })
      });
      
      setChatMessage("");
      // Show success toast or update UI
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingChat(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
          <p className="text-gray-500 font-mono text-sm">Loading portal...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Portal Not Found</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }
  
  const currentStage = getCurrentStage();
  const needsIntake = !portal?.intake_completed && portal?.status !== "active";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700">
              Pilot Partner
            </Badge>
            {user?.name && (
              <span className="text-sm text-gray-600 hidden sm:block">{user.name}</span>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Welcome Banner */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""} 👋
          </h1>
          <p className="text-gray-600">
            {needsIntake 
              ? "Complete your intake form to finish setting up your pilot experience."
              : "Your pilot portal is ready. Explore the platform and share your feedback."}
          </p>
        </div>

        {/* Intake Gate - Redirect to Visual Intake Builder if intake not complete */}
        {needsIntake ? (
          <Card className="shadow-lg border-0 overflow-hidden">
            <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-purple-600" />
                    Complete Your Intake
                  </CardTitle>
                  <CardDescription>
                    Walk us through your experience to personalize your pilot portal
                  </CardDescription>
                </div>
                <Badge variant="secondary">
                  5 Quick Sections
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="p-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Let's Get You Set Up</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Tell us about how you work with clients - from discovery to service day. 
                  This helps us personalize your Phygital experience.
                </p>
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  {["Discovery", "First Booking", "Service Day", "Returning Clients", "Your Setup"].map((section, idx) => (
                    <Badge key={section} variant="outline" className="text-xs">
                      {idx + 1}. {section}
                    </Badge>
                  ))}
                </div>
                <Button
                  onClick={() => navigate(`/pilot/intake/${portalId}`)}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8"
                  data-testid="start-intake-btn"
                >
                  Start Intake
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Portal Tabs - Show after intake complete */
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border p-1 rounded-xl flex-wrap">
              <TabsTrigger value="welcome" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                <Home className="w-4 h-4 mr-2" />
                Welcome
              </TabsTrigger>
              <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                <User className="w-4 h-4 mr-2" />
                My Profile
              </TabsTrigger>
              <TabsTrigger value="experience" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                <Sparkles className="w-4 h-4 mr-2" />
                Experience
              </TabsTrigger>
              <TabsTrigger value="chat" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat
              </TabsTrigger>
              <TabsTrigger value="updates" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                <Bell className="w-4 h-4 mr-2" />
                Updates
              </TabsTrigger>
            </TabsList>
            
            {/* Welcome Tab - Progress Roadmap */}
            <TabsContent value="welcome">
              <Card>
                <CardHeader>
                  <CardTitle>Your Pilot Journey</CardTitle>
                  <CardDescription>Track your progress through the pilot program</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {ONBOARDING_STAGES.map((stage, idx) => {
                      const Icon = stage.icon;
                      const isComplete = currentStage > stage.id;
                      const isCurrent = currentStage === stage.id;
                      
                      return (
                        <div key={stage.id} className="flex items-start gap-4">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                            isComplete 
                              ? "bg-green-500 text-white" 
                              : isCurrent 
                                ? "bg-purple-600 text-white" 
                                : "bg-gray-100 text-gray-400"
                          }`}>
                            {isComplete ? (
                              <CheckCircle2 className="w-5 h-5" />
                            ) : (
                              <Icon className="w-5 h-5" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-medium ${
                              isComplete ? "text-green-600" : isCurrent ? "text-purple-600" : "text-gray-400"
                            }`}>
                              {stage.label}
                            </h3>
                            {isCurrent && (
                              <p className="text-sm text-gray-500 mt-1">
                                {stage.id === 4 && "Share your feedback to help us improve."}
                                {stage.id === 5 && "You're almost ready to launch!"}
                              </p>
                            )}
                          </div>
                          {idx < ONBOARDING_STAGES.length - 1 && (
                            <div className={`absolute left-5 top-10 w-0.5 h-6 ${
                              isComplete ? "bg-green-500" : "bg-gray-200"
                            }`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Legacy Benefit Card */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center">
                        <Rocket className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-purple-900">Legacy Tier Unlocked</h3>
                        <p className="text-sm text-purple-700">Lifetime access to Phygital - Free forever</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      As a pilot partner, you've earned lifetime access to Phygital's Legacy tier. 
                      Thank you for helping us build something great.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* My Profile Tab - Show ALL intake fields (filled or not) */}
            <TabsContent value="profile">
              <MyProfileSection 
                portalId={portalId}
                portal={portal}
                user={user}
                intakeResponses={portal?.intake_responses || {}}
                onUpdate={(newResponses) => {
                  setPortal(prev => ({...prev, intake_responses: newResponses}));
                }}
              />
            </TabsContent>
            
            {/* Experience Tab - Demo Preview */}
            <TabsContent value="experience">
              <Card>
                <CardHeader>
                  <CardTitle>Preview the Experience</CardTitle>
                  <CardDescription>See what your clients will experience</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Coming Soon</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                      We're building an interactive preview of the client experience. 
                      Check back soon to test the flow your clients will see.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Chat Tab - With Bug/Feature/Quick Chat */}
            <TabsContent value="chat">
              <ChatSection 
                portalId={portalId}
                user={user}
              />
            </TabsContent>
            
            {/* Updates Tab - Synced from Admin */}
            <TabsContent value="updates">
              <UpdatesSection portalId={portalId} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// CHAT SECTION - Bug Reports, Feature Requests, Quick Chat
// =====================================================================
function ChatSection({ portalId, user }) {
  const [activeType, setActiveType] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch user's messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/portal/${portalId}/messages`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
        }
      } catch (err) {
        console.error("Failed to fetch messages:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [portalId]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newAttachments = [];
    
    for (const file of files) {
      // Create a preview URL for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          newAttachments.push({
            name: file.name,
            type: file.type,
            size: file.size,
            preview: e.target.result,
            file: file
          });
          if (newAttachments.length === files.length) {
            setAttachments(prev => [...prev, ...newAttachments]);
            setUploading(false);
          }
        };
        reader.readAsDataURL(file);
      } else {
        newAttachments.push({
          name: file.name,
          type: file.type,
          size: file.size,
          preview: null,
          file: file
        });
        if (newAttachments.length === files.length) {
          setAttachments(prev => [...prev, ...newAttachments]);
          setUploading(false);
        }
      }
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // First upload attachments if any
      const attachmentUrls = [];
      for (const attachment of attachments) {
        const formData = new FormData();
        formData.append("file", attachment.file);
        formData.append("type", "message_attachment");
        formData.append("portal_id", portalId);
        
        const uploadRes = await fetch(`${API}/api/messages/upload-attachment`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          attachmentUrls.push({
            url: uploadData.url,
            name: attachment.name,
            type: attachment.type
          });
        }
      }
      
      const res = await fetch(`${API}/api/portal/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          portal_id: portalId,
          message_type: activeType,
          title: activeType !== "chat" ? title : "",
          content: content,
          attachments: attachmentUrls,
          name: user?.name,
          email: user?.email,
          page_url: window.location.href,
          browser_info: navigator.userAgent
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [data.message, ...prev]);
        setTitle("");
        setContent("");
        setAttachments([]);
      }
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const typeConfig = {
    bug: { 
      label: "Report Bug", 
      icon: Bug, 
      placeholder: "Describe the issue...",
      titlePlaceholder: "Brief bug summary",
      color: "text-red-500",
      bgColor: "bg-red-50"
    },
    feature: { 
      label: "Request Feature", 
      icon: Sparkles, 
      placeholder: "Describe the feature...",
      titlePlaceholder: "Feature name",
      color: "text-purple-500",
      bgColor: "bg-purple-50"
    },
    chat: { 
      label: "Quick Chat", 
      icon: MessageCircle, 
      placeholder: "What's on your mind?",
      titlePlaceholder: "",
      color: "text-blue-500",
      bgColor: "bg-blue-50"
    }
  };

  const currentConfig = typeConfig[activeType];
  const TypeIcon = currentConfig.icon;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send Feedback</CardTitle>
        <CardDescription>Report bugs, request features, or ask questions</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Type Tabs */}
        <div className="flex gap-2 flex-wrap">
          {Object.entries(typeConfig).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveType(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors border ${
                  activeType === key
                    ? `${config.bgColor} ${config.color} border-current`
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
                data-testid={`chat-tab-${key}`}
              >
                <Icon className="w-4 h-4" />
                {config.label}
              </button>
            );
          })}
        </div>

        {/* Form */}
        <div className="space-y-4">
          {activeType !== "chat" && (
            <div>
              <Label className="text-sm font-medium">{activeType === "bug" ? "Bug Title" : "Feature Name"}</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={currentConfig.titlePlaceholder}
                className="mt-1"
                data-testid="chat-title-input"
              />
            </div>
          )}
          
          <div>
            <Label className="text-sm font-medium">
              {activeType === "bug" ? "Description" : activeType === "feature" ? "Details" : "Message"}
            </Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={currentConfig.placeholder}
              rows={5}
              className="mt-1"
              data-testid="chat-content-input"
            />
            {activeType === "bug" && (
              <p className="text-xs text-gray-500 mt-1">
                Include steps to reproduce if possible. Browser info is automatically attached.
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={!content.trim() || submitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="chat-submit-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send {currentConfig.label.split(" ")[0] === "Request" ? "Request" : currentConfig.label.split(" ")[1] || "Message"}
            </Button>
          </div>
        </div>

        {/* Previous Messages */}
        {messages.length > 0 && (
          <div className="border-t pt-6">
            <h4 className="font-medium text-sm text-gray-700 mb-4">Your Previous Messages</h4>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {messages.slice(0, 10).map((msg) => {
                const config = typeConfig[msg.message_type] || typeConfig.chat;
                const Icon = config.icon;
                return (
                  <div
                    key={msg.message_id}
                    className={`p-3 rounded-lg border ${config.bgColor}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${config.color}`} />
                      <span className={`text-xs font-medium ${config.color}`}>
                        {config.label}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                      {msg.status && msg.status !== "new" && (
                        <Badge variant="secondary" className="text-xs">
                          {msg.status.replace("_", " ")}
                        </Badge>
                      )}
                    </div>
                    {msg.title && <p className="font-medium text-sm">{msg.title}</p>}
                    <p className="text-sm text-gray-600 line-clamp-2">{msg.content}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// UPDATES SECTION - Synced from Admin Portal Updates
// =====================================================================
function UpdatesSection({ portalId }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const res = await fetch(`${API}/api/portal/${portalId}/updates`);
        if (res.ok) {
          const data = await res.json();
          setUpdates(data.updates || []);
        }
      } catch (err) {
        console.error("Failed to fetch updates:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUpdates();
  }, [portalId]);

  const typeConfig = {
    announcement: { icon: Bell, color: "text-blue-500", label: "Announcement" },
    feature: { icon: Sparkles, color: "text-purple-500", label: "New Feature" },
    bug_fix: { icon: Bug, color: "text-red-500", label: "Bug Fix" },
    improvement: { icon: Rocket, color: "text-green-500", label: "Improvement" },
    milestone: { icon: CheckCircle2, color: "text-pink-500", label: "Milestone" }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Platform Updates</CardTitle>
        <CardDescription>Latest news and feature releases</CardDescription>
      </CardHeader>
      <CardContent>
        {updates.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="font-medium">No updates yet</p>
            <p className="text-sm text-gray-500 mt-1">Check back later for news and updates</p>
          </div>
        ) : (
          <div className="space-y-4">
            {updates.map((update) => {
              const config = typeConfig[update.update_type] || typeConfig.announcement;
              const Icon = config.icon;
              return (
                <div
                  key={update.update_id}
                  className={`p-4 border rounded-lg ${update.is_pinned ? "border-purple-300 bg-purple-50/50" : ""}`}
                >
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className={config.color}>{config.label}</span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-400">
                      {update.published_at 
                        ? new Date(update.published_at).toLocaleDateString() 
                        : "Recently"}
                    </span>
                    {update.is_pinned && (
                      <Badge variant="secondary" className="ml-auto">Pinned</Badge>
                    )}
                  </div>
                  <h3 className="font-medium text-gray-900">{update.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{update.content}</p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// MY PROFILE SECTION - Shows ALL intake fields (filled or not)
// =====================================================================
function MyProfileSection({ portalId, portal, user, intakeResponses, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(intakeResponses || {});
  const { toast } = require("sonner");
  
  // Define ALL possible intake fields with groupings
  const INTAKE_FIELDS = [
    // Basic Info
    { key: "business_name", label: "Business Name", group: "Basic Info", type: "text" },
    { key: "full_name", label: "Your Name", group: "Basic Info", type: "text" },
    { key: "email", label: "Email", group: "Basic Info", type: "email" },
    { key: "phone", label: "Phone", group: "Basic Info", type: "tel" },
    { key: "website", label: "Website", group: "Basic Info", type: "url" },
    { key: "instagram", label: "Instagram Handle", group: "Basic Info", type: "text" },
    { key: "location", label: "Location/City", group: "Basic Info", type: "text" },
    
    // Service Details
    { key: "services_offered", label: "Services You Offer", group: "Services", type: "textarea" },
    { key: "specialties", label: "Specialties", group: "Services", type: "text" },
    { key: "years_experience", label: "Years of Experience", group: "Services", type: "number" },
    { key: "price_range", label: "Price Range", group: "Services", type: "text" },
    { key: "booking_platform", label: "Current Booking Platform", group: "Services", type: "text" },
    
    // Client Experience
    { key: "discovery_process", label: "How Clients Find You", group: "Client Experience", type: "textarea" },
    { key: "first_booking", label: "First Booking Process", group: "Client Experience", type: "textarea" },
    { key: "service_day", label: "Service Day Experience", group: "Client Experience", type: "textarea" },
    { key: "returning_clients", label: "Returning Client Experience", group: "Client Experience", type: "textarea" },
    
    // Setup & Preferences
    { key: "workspace_type", label: "Workspace Type", group: "Setup", type: "text" },
    { key: "team_size", label: "Team Size", group: "Setup", type: "text" },
    { key: "goals", label: "Goals for This Pilot", group: "Setup", type: "textarea" },
    { key: "challenges", label: "Current Challenges", group: "Setup", type: "textarea" },
    { key: "client_communication", label: "How You Communicate with Clients", group: "Setup", type: "textarea" },
    
    // Additional Info
    { key: "inspiration_sources", label: "Inspiration Sources", group: "Additional", type: "textarea" },
    { key: "tools_used", label: "Tools/Products Used", group: "Additional", type: "textarea" },
    { key: "additional_notes", label: "Anything Else to Share", group: "Additional", type: "textarea" },
  ];
  
  // Group fields
  const FIELD_GROUPS = [...new Set(INTAKE_FIELDS.map(f => f.group))];
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/pilot/portal/${portalId}/intake`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ intake_responses: formData })
      });
      
      if (res.ok) {
        toast.success("Profile updated! Admin has been notified.");
        onUpdate(formData);
        setEditing(false);
      } else {
        toast.error("Failed to save changes");
      }
    } catch (err) {
      toast.error("Error saving profile");
    } finally {
      setSaving(false);
    }
  };
  
  // Count filled fields
  const filledCount = INTAKE_FIELDS.filter(f => formData[f.key] && formData[f.key].toString().trim()).length;
  const totalCount = INTAKE_FIELDS.length;
  const completionPercent = Math.round((filledCount / totalCount) * 100);
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              My Profile
            </CardTitle>
            <CardDescription>
              Your intake responses and profile information
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium">{filledCount}/{totalCount} fields</p>
              <p className="text-xs text-gray-500">{completionPercent}% complete</p>
            </div>
            {editing ? (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  setFormData(intakeResponses || {});
                  setEditing(false);
                }}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save Changes
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Settings className="w-4 h-4 mr-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
        <Progress value={completionPercent} className="mt-4 h-2" />
      </CardHeader>
      
      <CardContent className="space-y-6">
        {FIELD_GROUPS.map(group => (
          <div key={group} className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">{group}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {INTAKE_FIELDS.filter(f => f.group === group).map(field => {
                const value = formData[field.key] || "";
                const hasValue = value.toString().trim().length > 0;
                
                return (
                  <div key={field.key} className={`${field.type === 'textarea' ? 'md:col-span-2' : ''}`}>
                    <Label className="text-xs text-gray-500 flex items-center gap-1">
                      {field.label}
                      {hasValue ? (
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                      ) : (
                        <Circle className="w-3 h-3 text-gray-300" />
                      )}
                    </Label>
                    
                    {editing ? (
                      field.type === 'textarea' ? (
                        <Textarea
                          value={value}
                          onChange={(e) => setFormData(prev => ({...prev, [field.key]: e.target.value}))}
                          placeholder={`Enter ${field.label.toLowerCase()}...`}
                          className="mt-1"
                          rows={3}
                        />
                      ) : (
                        <Input
                          type={field.type}
                          value={value}
                          onChange={(e) => setFormData(prev => ({...prev, [field.key]: e.target.value}))}
                          placeholder={`Enter ${field.label.toLowerCase()}...`}
                          className="mt-1"
                        />
                      )
                    ) : (
                      <div className={`mt-1 p-2 rounded border ${hasValue ? 'bg-white' : 'bg-gray-50 border-dashed'}`}>
                        {hasValue ? (
                          <p className="text-sm">{value}</p>
                        ) : (
                          <p className="text-sm text-gray-400 italic">Not filled yet</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        
        {/* Link to Phygital Profile */}
        {user && (
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-purple-900">Your Phygital Profile</h4>
                <p className="text-sm text-purple-700">View and edit your main Phygital profile</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-purple-700 border-purple-300"
                onClick={() => window.location.href = "/me"}
              >
                Go to /me
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
